import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { createPalette, type PaletteCommandDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';

import { apiClient, getBadgePropsFromUpgradeAction, useUpgrade } from '../../../state';

/**
 * Return a command to export a content as PDF.
 */
export function useExportPDFCommand({
    space,
    changeRequestId,
    pageId,
}: {
    space: api.Space;
    changeRequestId?: string;
    pageId?: string;
}): PaletteCommandDef {
    const upgrade = useUpgrade('pdf-export');

    const exportPDF = React.useCallback(
        async (page?: string, only?: boolean) => {
            if (upgrade) {
                return upgrade.onPress();
            }

            analytics.logEvent(
                changeRequestId
                    ? analytics.createChangeRequestEvent(space, changeRequestId, {
                          type: 'space_export',
                          source: 'pdf',
                      })
                    : analytics.createSpaceEvent(space, {
                          type: 'space_export',
                          source: 'pdf',
                      })
            );

            const opts = page ? { only, page } : {};
            const { data } = await (changeRequestId
                ? apiClient.changeRequests.getChangeRequestPdf(space.id, changeRequestId, opts)
                : apiClient.spaceContent.getSpacePdf(space.id, opts));

            window.open(data.url, '_blank');
        },
        [space, changeRequestId, upgrade]
    );

    return React.useMemo(() => {
        return {
            key: 'space.export',
            icon: Icon.PDF,
            title: 'Export as PDF',
            upgrade: Boolean(upgrade),
            badge: upgrade
                ? { ...getBadgePropsFromUpgradeAction({ upgrade }), label: 'Upgrade' }
                : undefined,
            descendant:
                pageId && !upgrade
                    ? () =>
                          createPalette({
                              static: true,
                              groups: [
                                  {
                                      key: 'pages',
                                      commands: [
                                          {
                                              key: 'pages.all',
                                              title: 'All pages',
                                              icon: Icon.PDF,
                                              trigger: () => exportPDF(),
                                          },
                                          {
                                              key: 'pages.current',
                                              title: 'Current page',
                                              icon: Icon.PDF,
                                              trigger: () => exportPDF(pageId, true),
                                          },
                                      ],
                                  },
                              ],
                          })
                    : undefined,
            trigger: () => exportPDF(pageId),
        };
    }, [pageId, exportPDF, upgrade]);
}
